export type ThemeMode = "light" | "dark";
export type ThemeDirection = "ltr" | "rtl";

export const THEME_STORAGE_KEY = "telegram-dashboard-theme";
export const PAGE_STORAGE_KEY = "telegram-dashboard-page";

/** Palette matches the settings customizer, with Telegram blue as brand */
export const THEME_COLORS = [
  { id: "blue", value: "#1a97f5" },
  { id: "indigo", value: "#3f51b5" },
  { id: "purple", value: "#9c27b0" },
  { id: "teal", value: "#00897b" },
  { id: "cyan", value: "#00bcd4" },
  { id: "coral", value: "#e57373" },
] as const;

export type ThemeColorId = (typeof THEME_COLORS)[number]["id"];

export interface ThemePreferences {
  mode: ThemeMode;
  direction: ThemeDirection;
  color: string;
}

export const DEFAULT_THEME: ThemePreferences = {
  mode: "dark",
  direction: "ltr",
  /** Telegram brand blue */
  color: "#229ED9",
};

export function isDefaultTheme(prefs: ThemePreferences): boolean {
  return (
    prefs.mode === DEFAULT_THEME.mode &&
    prefs.direction === DEFAULT_THEME.direction &&
    prefs.color.toLowerCase() === DEFAULT_THEME.color.toLowerCase()
  );
}

export function isThemeColor(value: string): boolean {
  const lower = value.toLowerCase();
  return (
    lower === DEFAULT_THEME.color.toLowerCase() ||
    THEME_COLORS.some((c) => c.value.toLowerCase() === lower)
  );
}

export function readStoredTheme(): ThemePreferences {
  if (typeof window === "undefined") return DEFAULT_THEME;
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (!raw) return DEFAULT_THEME;
    const parsed = JSON.parse(raw) as Partial<ThemePreferences>;
    return {
      mode: parsed.mode === "light" ? "light" : "dark",
      direction: parsed.direction === "rtl" ? "rtl" : "ltr",
      color:
        typeof parsed.color === "string" && isThemeColor(parsed.color)
          ? parsed.color
          : DEFAULT_THEME.color,
    };
  } catch {
    return DEFAULT_THEME;
  }
}

export function persistTheme(prefs: ThemePreferences): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore quota / private mode
  }
}

export function applyThemeToDocument(prefs: ThemePreferences): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("dark", prefs.mode === "dark");
  root.dir = prefs.direction;
  root.style.setProperty("--theme-accent", prefs.color);
}

/** Inline bootstrap to avoid theme flash before React hydrates */
export const THEME_BOOTSTRAP_SCRIPT = `(function(){try{var k=${JSON.stringify(THEME_STORAGE_KEY)};var d=${JSON.stringify(DEFAULT_THEME)};var t=JSON.parse(localStorage.getItem(k)||"null")||d;var mode=t.mode==="light"?"light":"dark";var dir=t.direction==="rtl"?"rtl":"ltr";var color=typeof t.color==="string"?t.color:d.color;var r=document.documentElement;r.classList.toggle("dark",mode==="dark");r.dir=dir;r.style.setProperty("--theme-accent",color);}catch(e){}})();`;
